import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Hero from '../common/Hero';
import {
    FaFilePdf,
    FaFileWord,
    FaFileExcel,
    FaFileImage,
    FaFileAlt,
    FaDownload,
    FaLock,
    FaFileCode,
    FaEye,
    FaShare,
    FaTimes,
    FaCopy,
    FaFacebook,
    FaTwitter,
    FaLinkedin,
    FaWhatsapp
} from 'react-icons/fa';
import DancingShield from "../common/DancingShield";

// --- Styled Components ---

const DocumentsContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
`;

const Section = styled.section`
    margin: 4rem 0;
`;

const SectionTitle = styled.h2`
    font-size: 2rem;
    margin-bottom: 2rem;
    color: #333;
    font-family: 'Playfair Display', serif;
    position: relative;

    &:after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 0;
        width: 60px;
        height: 3px;
        background-color: #6c5ce7;
    }
`;

const Introduction = styled.div`
    margin-bottom: 3rem;

    p {
        font-size: 1.1rem;
        line-height: 1.8;
        color: #555;
    }
`;

const CategoryTabs = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 2rem;
    border-bottom: 1px solid #eee;
    padding-bottom: 1rem;
`;

const CategoryTab = styled.button`
    padding: 0.75rem 1.5rem;
    background: ${props => props.$active ? '#6c5ce7' : 'transparent'};
    color: ${props => props.$active ? 'white' : '#333'};
    border: 2px solid ${props => props.$active ? '#6c5ce7' : '#ddd'};
    border-radius: 5px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.3s ease;
    text-transform: capitalize;

    &:hover {
        background: ${props => props.$active ? '#6c5ce7' : 'rgba(108, 92, 231, 0.1)'};
        border-color: #6c5ce7;
    }
`;

const SearchContainer = styled.div`
    margin-bottom: 2rem;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 1rem;

    &:focus {
        outline: none;
        border-color: #6c5ce7;
    }
`;

const DocumentsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 2rem;
`;

const DocumentCard = styled(motion.div)`
    background: white;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    height: 100%;
    border: ${props => props.$highlighted ? '2px solid #6c5ce7' : 'none'};
`;

const DocumentHeader = styled.div`
    padding: 1.5rem;
    display: flex;
    align-items: center;
    border-bottom: 1px solid #f0f0f0;

    .icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${props => {
    switch(props.fileType) {
        case 'pdf': return '#ff5252';
        case 'doc': case 'docx': return '#2196f3';
        case 'xls': case 'xlsx': return '#4caf50';
        case 'json': return '#607d8b';
        case 'img': case 'jpg': case 'png': return '#ff9800';
        default: return '#9e9e9e';
    }
}};
        color: white;
        border-radius: 8px;
        margin-right: 1rem;
        font-size: 1.2rem;
    }

    .document-info {
        flex: 1;
        overflow: hidden;

        h3 {
            margin-bottom: 0.25rem;
            color: #333;
            font-size: 1.1rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .document-meta {
            display: flex;
            align-items: center;
            color: #888;
            font-size: 0.85rem;

            span {
                display: flex;
                align-items: center;
                margin-right: 1rem;

                svg {
                    margin-right: 0.25rem;
                }
            }
        }
    }
`;

const DocumentContent = styled.div`
    padding: 1.5rem;
    flex: 1;
    display: flex;
    flex-direction: column;

    p {
        color: #666;
        line-height: 1.6;
        margin-bottom: 1.5rem;
        flex: 1;
        font-size: 0.95rem;
    }
`;

const DocumentFooter = styled.div`
    padding: 1rem 1.5rem;
    background-color: #f9f9f9;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .document-date {
        color: #888;
        font-size: 0.85rem;
    }

    .document-actions {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
    }
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    background-color: ${props => props.disabled ? '#f0f0f0' : (props.variant === 'primary' ? '#6c5ce7' : 'white')};
    color: ${props => props.disabled ? '#888' : (props.variant === 'primary' ? 'white' : '#6c5ce7')};
    border: ${props => props.variant === 'primary' ? 'none' : '1px solid #6c5ce7'};
    padding: 0.5rem 0.8rem;
    border-radius: 5px;
    font-weight: 500;
    font-size: 0.9rem;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.3s ease;
    flex: 1;
    justify-content: center;
    text-decoration: none;

    svg {
        margin-right: 0.4rem;
    }

    &:hover:not(:disabled) {
        background-color: ${props => props.variant === 'primary' ? '#5a4ad1' : 'rgba(108, 92, 231, 0.1)'};
    }
`;

// Modal Styles
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 2rem;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 10px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
`;

const ModalHeader = styled.div`
    padding: 1.5rem;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
        margin: 0;
        flex: 1;
        color: #333;
    }

    button {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: #666;
        padding: 0.5rem;
        margin-left: 1rem;

        &:hover {
            color: #333;
        }
    }
`;

const DocumentViewer = styled.div`
    padding: 2rem;
    text-align: center;

    iframe {
        width: 100%;
        height: 500px;
        border: none;
        border-radius: 5px;
    }

    .preview-unavailable {
        padding: 3rem;
        color: #666;
        background: #f9f9f9;
        border-radius: 5px;
        margin: 1rem 0;

        svg {
            font-size: 3rem;
            margin-bottom: 1rem;
            color: #ddd;
        }
    }
`;

// Share Modal Styles
const ShareModalContent = styled.div`
    padding: 2rem;

    .share-url {
        display: flex;
        gap: 0.5rem;
        margin: 1rem 0;

        input {
            flex: 1;
            padding: 0.75rem;
            border: 1px solid #ddd;
            border-radius: 5px;
            font-size: 0.9rem;
        }

        button {
            padding: 0.75rem 1rem;
            background: #6c5ce7;
            color: white;
            border: none;
            border-radius: 5px;
            cursor: pointer;

            &:hover {
                background: #5a4ad1;
            }
        }
    }

    .social-share {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 1rem;
        margin-top: 1.5rem;

        button {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.75rem;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-weight: 500;
            transition: all 0.3s ease;

            svg {
                margin-right: 0.5rem;
                font-size: 1.1rem;
            }

            &.facebook {
                background: #3b5998;
                color: white;
                &:hover { background: #2d4373; }
            }

            &.twitter {
                background: #1da1f2;
                color: white;
                &:hover { background: #1a91da; }
            }

            &.linkedin {
                background: #0077b5;
                color: white;
                &:hover { background: #005885; }
            }

            &.whatsapp {
                background: #25d366;
                color: white;
                &:hover { background: #20b858; }
            }
        }
    }
`;

const NoDocuments = styled.div`
    grid-column: 1 / -1;
    text-align: center;
    padding: 3rem;
    color: #666;
    background: white;
    border-radius: 10px;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 3rem;
    font-size: 1.2rem;
    color: #666;
`;

const ErrorMessage = styled.div`
    text-align: center;
    padding: 2rem;
    color: #e74c3c;
    background: #fdeaea;
    border-radius: 8px;
    margin-bottom: 2rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 3rem;
  gap: 1rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #6c5ce7;
  background-color: ${props => props.disabled ? 'transparent' : (props.$active ? '#6c5ce7' : 'transparent')};
  color: ${props => props.disabled ? '#ccc' : (props.$active ? '#fff' : '#6c5ce7')};
  border-color: ${props => props.disabled ? '#ccc' : '#6c5ce7'};
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 600;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #6c5ce7;
    color: #fff;
  }
`;

const SharedDocumentBanner = styled.div`
    background: linear-gradient(135deg, #6c5ce7, #a8e6cf);
    color: white;
    padding: 1rem 2rem;
    border-radius: 8px;
    margin-bottom: 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .banner-content {
        h4 {
            margin: 0 0 0.5rem 0;
            font-size: 1.1rem;
        }

        p {
            margin: 0;
            opacity: 0.9;
        }
    }

    .banner-close {
        background: rgba(255, 255, 255, 0.2);
        border: none;
        color: white;
        padding: 0.5rem;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
    }
`;

const API_ENDPOINT = 'https://z184yo4b1e.execute-api.eu-west-1.amazonaws.com/prod/mlalazi/documents';

const DocumentsPage = () => {
    // React Router hooks for query params
    const location = useLocation();
    const navigate = useNavigate();

    // 1. Data State
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 2. Filter State
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    // 3. Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // 4. Modal State
    const [viewModal, setViewModal] = useState({ isOpen: false, document: null });
    const [shareModal, setShareModal] = useState({ isOpen: false, document: null });

    // 5. Shared Document State
    const [sharedDocument, setSharedDocument] = useState(null);
    const [sharedDocumentInfo, setSharedDocumentInfo] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);

    // Parse query parameters
    const queryParams = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return {
            docId: params.get('doc'),
            title: params.get('title'),
            category: params.get('category')
        };
    }, [location.search]);

    // Fetch Data
    useEffect(() => {
        const fetchDocuments = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_ENDPOINT}?limit=100`);

                if (!response.ok) {
                    throw new Error('Failed to fetch documents');
                }

                const data = await response.json();

                let docsArray = [];
                if (Array.isArray(data)) {
                    docsArray = data;
                } else if (data && Array.isArray(data.data)) {
                    docsArray = data.data;
                }

                setDocuments(docsArray);
                setError(null);
            } catch (err) {
                console.error("Error fetching documents:", err);
                setError(err.message);
                setDocuments([]);
            } finally {
                setLoading(false);
            }
        };

        fetchDocuments();
    }, []);

    // Handle shared document links from query params
    useEffect(() => {
        if (queryParams.docId && documents.length > 0) {
            // Store the shared document info from URL params
            setSharedDocumentInfo({
                id: queryParams.docId,
                title: queryParams.title || 'Shared Document',
                category: queryParams.category || 'all'
            });

            // Find the document by ID
            const foundDocument = documents.find(
                doc => String(doc.id) === String(queryParams.docId)
            );

            if (foundDocument) {
                setSharedDocument(foundDocument);

                // Auto-open the shared document in view modal
                setViewModal({ isOpen: true, document: foundDocument });

                // Set the category to match the shared document
                if (queryParams.category && queryParams.category.toLowerCase() !== 'all') {
                    // Match category case-insensitively
                    const matchingCategory = categories.find(
                        cat => cat.name.toLowerCase() === queryParams.category.toLowerCase() ||
                            cat.id.toLowerCase() === queryParams.category.toLowerCase()
                    );
                    if (matchingCategory) {
                        setActiveCategory(matchingCategory.id);
                    }
                }
            } else {
                // Document not found in the list - show banner with info from URL
                console.warn(`Document with ID ${queryParams.docId} not found`);
            }
        }
    }, [queryParams, documents]);

    // Clear URL params after handling (optional - keeps URL clean)
    const clearQueryParams = () => {
        navigate(location.pathname, { replace: true });
        setSharedDocument(null);
        setSharedDocumentInfo(null);
    };

    const categories = [
        { id: 'all', name: 'All Documents' },
        { id: 'history', name: 'History' },
        { id: 'meetings', name: 'Meetings' },
        { id: 'events', name: 'Events' },
        { id: 'news', name: 'News Letters' },
        { id: 'legal', name: 'Legal Documents' },
    ];

    // Filter Logic
    const filteredDocuments = documents.filter(doc => {
        const docTitle = (doc.title || '').toLowerCase();
        const docDescription = (doc.description || '').toLowerCase();

        const rawCategory = doc.category || '';
        const docCategory = rawCategory.trim().toLowerCase();
        const selectedCategory = activeCategory.trim().toLowerCase();

        const matchesCategory = selectedCategory === 'all' || docCategory === selectedCategory;
        const matchesSearch = docTitle.includes(searchTerm.toLowerCase()) ||
            docDescription.includes(searchTerm.toLowerCase());

        return matchesCategory && matchesSearch;
    });

    // Reset to page 1 whenever filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeCategory, searchTerm]);

    const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedDocuments = filteredDocuments.slice(startIndex, startIndex + itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    // Modal Handlers
    const handleView = (document) => {
        setViewModal({ isOpen: true, document });
    };

    const handleShare = (document) => {
        setShareModal({ isOpen: true, document });
        setCopySuccess(false);
    };

    const closeModals = () => {
        setViewModal({ isOpen: false, document: null });
        setShareModal({ isOpen: false, document: null });
        setCopySuccess(false);
    };

    // Generate shareable URL that points back to this page
    const generateShareUrl = (document) => {
        const baseUrl = `${window.location.origin}${window.location.pathname}`;
        const params = new URLSearchParams({
            doc: document.id,
            title: document.title,
            category: document.category || 'all'
        });
        return `${baseUrl}?${params.toString()}`;
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 3000);
        } catch (err) {
            console.error('Failed to copy: ', err);
            // Fallback for browsers that don't support clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 3000);
            } catch (fallbackErr) {
                console.error('Fallback copy failed: ', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    const handleSocialShare = (platform, document) => {
        const url = generateShareUrl(document);
        const title = `${document.title} - Clan Documents`;
        const description = document.description || 'View this important clan document';

        let shareUrl;
        switch (platform) {
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`;
                break;
            case 'twitter':
                shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
                break;
            case 'linkedin':
                shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(description)}`;
                break;
            case 'whatsapp':
                shareUrl = `https://wa.me/?text=${encodeURIComponent(`${title}\n${description}\n${url}`)}`;
                break;
            default:
                return;
        }

        window.open(shareUrl, '_blank', 'width=600,height=400');
    };

    // Helpers
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown Date';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const getFileIcon = (fileType) => {
        const type = (fileType || 'unknown').toLowerCase();
        if (type.includes('pdf')) return <FaFilePdf />;
        if (type.includes('doc')) return <FaFileWord />;
        if (type.includes('xls')) return <FaFileExcel />;
        if (type.includes('json')) return <FaFileCode />;
        if (type.includes('img') || type.includes('jpg') || type.includes('png')) return <FaFileImage />;
        return <FaFileAlt />;
    };

    const getDownloadUrl = (url) => {
        if (!url) return '#';
        return url;
    };

    const canPreviewDocument = (document) => {
        const fileType = (document.fileType || '').toLowerCase();
        return fileType.includes('pdf') ||
            fileType.includes('doc') ||
            fileType.includes('txt') ||
            fileType.includes('img') ||
            fileType.includes('jpg') ||
            fileType.includes('png');
    };

    // Check if a document is the shared one (for highlighting)
    const isSharedDocument = (doc) => {
        return sharedDocument && String(doc.id) === String(sharedDocument.id);
    };

    return (
        <>
            <Hero
                title="Official Documents"
                subtitle="Access, view, and share important clan documents"
                backgroundImage="/assets/images/logo.jpg"
            />

            <DocumentsContainer>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Show shared document banner */}
                    {(sharedDocument || sharedDocumentInfo) && (
                        <SharedDocumentBanner>
                            <div className="banner-content">
                                <h4>📄 Shared Document</h4>
                                <p>
                                    {sharedDocument
                                        ? `Someone shared "${sharedDocument.title}" with you.`
                                        : `Someone shared "${sharedDocumentInfo?.title}" with you.`
                                    }
                                    {sharedDocument && ' The document viewer is open.'}
                                </p>
                            </div>
                            <button
                                className="banner-close"
                                onClick={clearQueryParams}
                                title="Dismiss"
                            >
                                <FaTimes />
                            </button>
                        </SharedDocumentBanner>
                    )}

                    <Introduction>
                        <SectionTitle>Clan Document Repository</SectionTitle>
                        <p>
                            Our clan document repository provides centralized access to important documents, forms, and records.
                            You can view, download, and share documents with clan members.
                        </p>
                    </Introduction>

                    <Section>
                        <CategoryTabs>
                            {categories.map(category => (
                                <CategoryTab
                                    key={category.id}
                                    type="button"
                                    $active={activeCategory === category.id}
                                    onClick={() => setActiveCategory(category.id)}
                                >
                                    {category.name}
                                </CategoryTab>
                            ))}
                        </CategoryTabs>

                        <SearchContainer>
                            <SearchInput
                                type="text"
                                placeholder="Search documents..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </SearchContainer>

                        {loading && <LoadingMessage><DancingShield />Loading documents...</LoadingMessage>}

                        {error && (
                            <ErrorMessage>
                                <DancingShield />
                                Unable to load documents. Please refresh the page.
                            </ErrorMessage>
                        )}

                        {!loading && !error && (
                            <>
                                {displayedDocuments.length > 0 ? (
                                    <DocumentsGrid>
                                        {displayedDocuments.map(document => (
                                            <DocumentCard
                                                key={document.id}
                                                $highlighted={isSharedDocument(document)}
                                                whileHover={{ y: -5 }}
                                                initial={{ opacity: 0, y: 20 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 0.5 }}
                                            >
                                                <DocumentHeader fileType={document.fileType}>
                                                    <div className="icon">
                                                        {getFileIcon(document.fileType)}
                                                    </div>
                                                    <div className="document-info">
                                                        <h3 title={document.title}>{document.title}</h3>
                                                        <div className="document-meta">
                                                            <span>
                                                                {getFileIcon(document.fileType)} {(document.fileType || 'FILE').toUpperCase()}
                                                            </span>
                                                            <span>
                                                                <FaFileAlt /> {document.fileSize || 'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </DocumentHeader>
                                                <DocumentContent>
                                                    <p>{document.description}</p>
                                                </DocumentContent>
                                                <DocumentFooter>
                                                    <div className="document-date">
                                                        {formatDate(document.uploadDate)}
                                                    </div>
                                                    <div className="document-actions">
                                                        <ActionButton
                                                            onClick={() => handleView(document)}
                                                            disabled={document.restricted}
                                                            variant="secondary"
                                                        >
                                                            <FaEye /> View
                                                        </ActionButton>
                                                        <ActionButton
                                                            as="a"
                                                            href={document.restricted ? "#" : getDownloadUrl(document.downloadUrl)}
                                                            download={!document.restricted}
                                                            disabled={document.restricted}
                                                            target={!document.restricted ? "_blank" : undefined}
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => document.restricted && e.preventDefault()}
                                                            variant="primary"
                                                        >
                                                            {document.restricted ? (
                                                                <>
                                                                    <FaLock /> Restricted
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FaDownload /> Download
                                                                </>
                                                            )}
                                                        </ActionButton>
                                                        <ActionButton
                                                            onClick={() => handleShare(document)}
                                                            disabled={document.restricted}
                                                            variant="secondary"
                                                        >
                                                            <FaShare /> Share
                                                        </ActionButton>
                                                    </div>
                                                </DocumentFooter>
                                            </DocumentCard>
                                        ))}
                                    </DocumentsGrid>
                                ) : (
                                    <NoDocuments>
                                        <DancingShield />
                                        <p>No documents found.</p>
                                    </NoDocuments>
                                )}

                                {displayedDocuments.length > 0 && totalPages > 1 && (
                                    <PaginationContainer>
                                        <PageButton
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </PageButton>

                                        <span>Page {currentPage} of {totalPages}</span>

                                        <PageButton
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </PageButton>
                                    </PaginationContainer>
                                )}
                            </>
                        )}
                    </Section>
                </motion.div>
            </DocumentsContainer>

            {/* View Modal */}
            {viewModal.isOpen && (
                <ModalOverlay onClick={closeModals}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>{viewModal.document.title}</h3>
                            <button onClick={closeModals}>
                                <FaTimes />
                            </button>
                        </ModalHeader>
                        <DocumentViewer>
                            {canPreviewDocument(viewModal.document) && viewModal.document.downloadUrl ? (
                                <iframe
                                    src={viewModal.document.downloadUrl}
                                    title={viewModal.document.title}
                                />
                            ) : (
                                <div className="preview-unavailable">
                                    <FaFileAlt />
                                    <h4>Preview not available</h4>
                                    <p>This document type cannot be previewed in the browser.</p>
                                    {!viewModal.document.restricted && (
                                        <ActionButton
                                            as="a"
                                            href={getDownloadUrl(viewModal.document.downloadUrl)}
                                            download
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="primary"
                                        >
                                            <FaDownload /> Download to View
                                        </ActionButton>
                                    )}
                                </div>
                            )}
                        </DocumentViewer>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* Share Modal */}
            {shareModal.isOpen && (
                <ModalOverlay onClick={closeModals}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>Share Document</h3>
                            <button onClick={closeModals}>
                                <FaTimes />
                            </button>
                        </ModalHeader>
                        <ShareModalContent>
                            <p>Share "{shareModal.document.title}" with others:</p>

                            <div className="share-url">
                                <input
                                    type="text"
                                    value={generateShareUrl(shareModal.document)}
                                    readOnly
                                />
                                <button onClick={() => copyToClipboard(generateShareUrl(shareModal.document))}>
                                    <FaCopy /> {copySuccess ? 'Copied!' : 'Copy'}
                                </button>
                            </div>

                            {copySuccess && (
                                <p style={{ color: '#4caf50', fontSize: '0.9rem', textAlign: 'center', margin: '0.5rem 0' }}>
                                    ✓ Link copied to clipboard!
                                </p>
                            )}

                            <div className="social-share">
                                <button
                                    className="facebook"
                                    onClick={() => handleSocialShare('facebook', shareModal.document)}
                                >
                                    <FaFacebook />
                                    Facebook
                                </button>
                                <button
                                    className="twitter"
                                    onClick={() => handleSocialShare('twitter', shareModal.document)}
                                >
                                    <FaTwitter />
                                    Twitter
                                </button>
                                <button
                                    className="linkedin"
                                    onClick={() => handleSocialShare('linkedin', shareModal.document)}
                                >
                                    <FaLinkedin />
                                    LinkedIn
                                </button>
                                <button
                                    className="whatsapp"
                                    onClick={() => handleSocialShare('whatsapp', shareModal.document)}
                                >
                                    <FaWhatsapp />
                                    WhatsApp
                                </button>
                            </div>
                        </ShareModalContent>
                    </ModalContent>
                </ModalOverlay>
            )}
        </>
    );
};

export default DocumentsPage;